/* eslint-disable react-refresh/only-export-components */
import dayjs from "dayjs"
import {
  CategoryAssociationPayloadType,
  FilterType,
  UpdateCategoryAssociationPayloadType,
  getCategoryListPayloadType,
  getTransactionListPayloadType,
} from "../slices/transactionsSlice"
import {
  MarkedStatus,
  TransactionType,
  transactionTypeLabels,
} from "../constants/data"

export const constructApiRequestParams = (
  filters: FilterType,
  pagination: any,
) => {
  const requestParams = {
    ...filters,
    ...pagination,
    accountId: filters.accountId,
    search: filters.search,
    startDate: dayjs(filters.startDate).format("YYYY-MM-DD"),
    endDate: dayjs(filters.endDate).format("YYYY-MM-DD"),
  }
  return requestParams
}

export const filterService = ({ payload }: getTransactionListPayloadType) => {
  return {
    page_no: payload.page + 1,
    page_size: payload.pageSize,
    ...(payload.isMarked &&
      payload.isMarked !== "all" && {
        ...(payload.isMarked === MarkedStatus.PENDING
          ? { is_marked: false, is_reconciled: true }
          : { is_marked: payload.isMarked }),
      }),
    start_date: payload.startDate,
    ...(payload.operator !== "" && { operator: payload.operator }),
    end_date: payload.endDate,
    ...(payload.accountId && { bank_account_id: payload.accountId }),
    ...(payload.searchType &&
      payload.search !== "" && { [payload.searchType]: payload.search }),
    ...(payload.amount !== "" && { amount: Number(payload.amount) }),
    ...(payload.transactionType &&
      payload.transactionType !== "all" && {
        transaction_type: payload.transactionType,
      }),
    ...(payload.entityType &&
      payload.entityType !== "all" && {
        entity: payload.entityType,
      }),
    ...(payload.sortBy !== "none" && {
      sort_by: payload.sortBy,
      order_by: payload.orderBy,
    }),
    ...(payload.paymentMethod &&
      payload.paymentMethod !== "all" && {
        payment_method: payload.paymentMethod,
      }),
    ...(payload.category !== "" && {
      level_1: payload.category,
    }),
    ...(payload.subCategory !== "" && {
      level_2: payload.subCategory,
    }),
    ...(payload.subCategory1 !== "" && {
      level_3: payload.subCategory1,
    }),
    ...(payload.minAmount && {
      amount_from: payload.minAmount,
    }),
    ...(payload.maxAmount && {
      amount_to: payload.maxAmount,
    }),
    ...(payload.contact !== "none" && { contact_identified: payload.contact }),
    ...(payload.removeSelfTransfer !== null && {
      remove_self_transfer: payload.removeSelfTransfer,
    }),
  }
}

export const titleCase = (str: string) => {
  return str
    ?.toLowerCase()
    .split(" ")
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(" ")
}

interface Invoice {
  invoiceNumber: string
}

export function hasDuplicateInvoiceNumber(
  invoices: Invoice[] | undefined,
): boolean {
  if (invoices === undefined) return false

  const invoiceNumbers: Record<string, boolean> = {}

  for (const invoice of invoices) {
    const { invoiceNumber } = invoice

    if (invoiceNumbers[invoiceNumber]) {
      return true
    } else {
      invoiceNumbers[invoiceNumber] = true
    }
  }

  return false
}

export function getTransactionTypeDisplayValue(data: TransactionType): string {
  return transactionTypeLabels[data] || "--"
}

export function getTransactionDateDisplayValue(date: string): string {
  return dayjs.utc(date).utcOffset("+05:30").format("DD/MM/YYYY HH:mm:ss")
}

export function getMarkedStatus(
  isMarked: boolean,
  isReconciled: boolean,
): MarkedStatus {
  if (isMarked) {
    return MarkedStatus.MARKED
  } else if (!isMarked && isReconciled) {
    return MarkedStatus.PENDING
  } else {
    return MarkedStatus.UNMARKED
  }
}

export function shouldShowReconcileButton({
  isMarked,
  isReconciled,
  type,
  isSynced,
  zohoTransactionId,
}: {
  isMarked: boolean
  isReconciled: boolean
  type: string
  isSynced: boolean
  zohoTransactionId: string
}) {
  if (
    isMarked ||
    isReconciled ||
    type !== TransactionType.INFLOW ||
    !isSynced ||
    !zohoTransactionId
  ) {
    return false
  }
  return true
}

export const CategoryListPayloadService = ({
  payload,
}: getCategoryListPayloadType) => {
  return {
    ...(payload.level1 !== "" && { level_1: payload.level1 }),
    ...(payload.level2 !== "" && { level_2: payload.level2 }),
  }
}

export const CategoriseTransactionsPayloadService = ({
  category,
  transactions,
}: CategoryAssociationPayloadType) => {
  return {
    level_1: category.category,
    ...(category.subCategory !== "" && { level_2: category.subCategory }),
    ...(category.subCategory1 !== "" && { level_3: category.subCategory1 }),
    transaction_ids: transactions,
  }
}
export const UpdateCategoriseTransactionsPayloadService = ({
  categories,
  transaction,
}: UpdateCategoryAssociationPayloadType) => {
  return {
    transactionId: transaction,
    payload: {
      categories: categories.filter((category) => category !== ""),
    },
  }
}
